import React, { useState } from 'react';
import { Button, FormGroup } from 'reactstrap';
import { NotificationManager } from 'react-notifications';
import { CSVToMatrix, MatrixToJSON } from '../csvUtils';
import { ASSIGNED_STATUS_FILTERS, COMPLETED_STATUS_FILTERS } from '../Tables/ListFilters';
import moment from 'moment';

export const CSVImport = ({ client, setHasSearchBeenClicked, setIsLoading, setRefresh, subStatuses, ...props }) => {
  const hiddenFileInput = React.useRef(null);

  const onClick = () => {
    hiddenFileInput.current.click();
  };

  const verifyStatus = (status) => {
    // Workaround as requested to allow submitting the unassigned order template without removing other job numbers
    if (status === 'Pending Allocation') {
      return true;
    } else {
      return [...ASSIGNED_STATUS_FILTERS, ...COMPLETED_STATUS_FILTERS].find(item => item.text === status);
    }
  };

  const verifySubStatus = (status, subStatus, subStatuses, jobNumber, errors) => {
    const hasSubStatus = subStatuses.some(item => item.type.text === status);
    if (!hasSubStatus) {
      if (subStatus) {
        errors.subStatus = `Job ${jobNumber} status ${status} does not support subStatus`;
        return false;
      }
      return true;
    }
    const statusItem = subStatuses.find(item => item.type.text === status);
    const check = statusItem.subStatuses.some(subItem => subItem.text === subStatus);
    if (!check) {
      errors.subStatus = `Job ${jobNumber} Invalid Sub Status for status ${status}`;
    }
  }

  const verifyDate = (dateString) => {
    const dateFormat = /^(0?[1-9]|[12][0-9]|3[01])[\/](0?[1-9]|1[012])[\/]\d{4}$/;
    return dateString.match(dateFormat);
  };

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const binaryString = e.target.result;
      const matrix = CSVToMatrix(binaryString, ',');
      const json = MatrixToJSON(matrix, 1);
      const { payload, errors } = verifyData(json);

      if (payload.length > 0) {
        client.put(`installer-jobs`, payload).tap(() => {
          if (Object.keys(errors).length > 0) {
            NotificationManager.warning(`CSV successfully imported ${payload.length} jobs, with ${Object.keys(errors).length} failed jobs`, 0, 0);
          } else {
            NotificationManager.success(`CSV successfully imported ${payload.length} jobs`, 0, 0);
          }
          setIsLoading(true);
          setHasSearchBeenClicked(true);
          setRefresh(moment());
        }).catch((error) => {
          NotificationManager.error(error.response.data, 0, 0);
        });
      }
    };
    reader.readAsText(file);
  };

  const verifyData = (data) => {
    const payload = [];
    const errorGroups = {};
    const errors = {};
    data.forEach((job, index) => {


      // Handle empty cells/values in the csv
      if (!Object.values(job).some((value) => value.trim())) {
        return;
      };

      let status_code;
      const statusValue = [...ASSIGNED_STATUS_FILTERS, ...COMPLETED_STATUS_FILTERS].find(item => item.text === job.jobStatus);
      // Workaround as requested to allow submitting the unassigned order template without removing other job numbers
      if (job.jobStatus === 'Pending Allocation') {
        status_code = 10;
      } else if (statusValue) {
        status_code = statusValue.value;
      }

      let substatus_code = null;
      const subStatusValue = subStatuses.find(item => item.type.text === job.jobStatus);
      if (subStatusValue) {
        const subStatusItem = subStatusValue.subStatuses.find(item => item.text === job.jobSubStatus);
        if (subStatusItem) {
          substatus_code = subStatusItem.value;
        }
      }

      let job_item = {
        "pk": job.jobNumber,
        "job_status": status_code,
        "job_substatus": substatus_code,
        "appointment_date": null,
        "job_completed_at": null,
      }

      if (!job.jobNumber && (job.jobStatus || job.bookedDate || job.completedDate)) {
        errors.jobNumber = `Row ${index + 1} must have a job number`;
      } else {
        if (!verifyStatus(job.jobStatus)) {
          errors.jobStatus = `Job ${job.jobNumber} Invalid Job Status`;
        }

        verifySubStatus(job.jobStatus, job.jobSubStatus, subStatuses, job.jobNumber, errors);

        if (ASSIGNED_STATUS_FILTERS.find(item => item.text === job.jobStatus)) {
          if ([40, 41, 76, 78].includes(status_code)) {
            if (!job.bookedDate) {
              errors.bookedDate = `Job ${job.jobNumber} must have a booked date`;
            } else if (!verifyDate(job.bookedDate)) {
              errors.bookedDate = `Job ${job.jobNumber} invalid booked Date,  must be in format of DD/MM/YYYY`;
            } else {
              job_item.appointment_date = moment(job.bookedDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
            }
          }
        }

        if (COMPLETED_STATUS_FILTERS.find(item => item.text === job.jobStatus)) {
          if ([50, 51, 52, 70, 71, 77, 79, 80].includes(status_code)) {
            if (!job.completedDate) {
              errors.completedDate = `Job ${job.jobNumber} must have a completed date`;
            } else if (!verifyDate(job.completedDate)) {
              errors.completedDate = `Job ${job.jobNumber} invalid completed Date,  must be in format of DD/MM/YYYY`;
            } else {
              job_item.job_completed_at = moment(job.completedDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
            }
          }
        }
      }

      // If there are no errors push job_item to payload
      if (Object.keys(errors).length === 0) {
        payload.push(job_item)
      } else {
        // Else group each error to their error type
        Object.keys(errors).forEach((errorType) => {
          if (!errorGroups[errorType]) {
            errorGroups[errorType] = [];
          }
          if (!errorGroups[errorType].includes(errors[errorType])) {
            errorGroups[errorType].push(errors[errorType]);
          }
        });
      }
    });

    // Create a notification for each error type
    Object.keys(errorGroups).forEach((errorType) => {
      if (errorGroups[errorType].length > 0) {
        NotificationManager.error(errorGroups[errorType].join('\n'), errorType, 0,
          () => {
            // Close all notifications if one notification is clicked
            NotificationManager.listNotify.forEach(n => NotificationManager.remove({ id: n.id }));
          },
        );
      }
    });
    return { payload, errors };
  };

  const onChange = (event) => {
    const fileUploaded = event.target.files[0];
    handleFile(fileUploaded);
    event.target.value = '';
  };

  return (
    <FormGroup {...props} className="job-status-template clearfix">
      <Button className="pull-right" color="primary" onClick={onClick}>
        Import CSV
      </Button>
      <input
        type="file"
        ref={hiddenFileInput}
        onChange={onChange}
        style={{ display: 'none' }}
        accept=".csv"
      />
    </FormGroup>
  );
};

export default CSVImport;



// WEBPACK FOOTER //
// ./src/components/common/JobStatusUpdate/CSVImport.jsx